import type { Direction, EventSource, EventStatus, ListResp } from '@lib/care-model';

/**
 * queue-cache - the queue reads' shared memory, one module for every reader (row 866, William 4 Oct
 * 2026: "Show the real count").
 *
 * Until row 866 this cache lived inside useEventQueue.ts, where only a page could reach it. The left
 * column's count for 01 Inbound queue (lib/inbound-open-count.ts) now reads the SAME answers the
 * Inbound queue page reads, so while the page is open the column asks the server for nothing. Two
 * things are kept per query shape:
 *
 *   - THE LAST GOOD ANSWER: the page's stale-while-revalidate cache (rev7), unchanged. Every answer is
 *     kept, a superseded one included, because it is still the freshest data for its own shape.
 *   - HOW THE NEWEST READ ENDED (ok or failed, and when), and how many reads are in flight. A failed
 *     read never replaces the last good answer (the page keeps showing its rows), but a reader that
 *     must never show a stale number can tell a fresh answer from one the server failed to renew.
 *
 * Nothing here fetches: the callers fetch and report each read with readStarted, then readSucceeded
 * or readFailed. "Newest" is by the order the reads STARTED (the ticket), so a slow old read that
 * lands after a newer one can never overrule it. A ticket ends ONCE: a second report of the same read
 * (a failure thrown after its success was reported, say) changes nothing.
 */

/** Every field of a queue read the server filters on. The key below names each one. */
export interface QueueShape {
  direction?: Direction | undefined;
  status?: EventStatus | undefined;
  source?: EventSource | undefined;
  notSource?: EventSource | readonly EventSource[] | undefined;
  excludeFaxFailures?: boolean | undefined;
  groupBy?: 'thread' | undefined;
  q?: string | undefined;
}

/**
 * The cache key of a read (moved from useEventQueue.ts; the string is unchanged). Every field of the
 * query shape must appear here: the Calling page (source=out-calling) and the member queue
 * (notSource=out-calling) differ ONLY by these, and a shared key would let each serve the other's rows.
 * The selected client leads the key: switching clients must never serve the other's rows.
 */
export function queueCacheKey(client: string, s: QueueShape): string {
  return `${client}|${s.direction ?? ''}|${s.status ?? ''}|${s.source ?? ''}|${s.notSource ?? ''}|${
    s.excludeFaxFailures ? 'nofax' : ''
  }|${s.groupBy ?? ''}|${s.q ?? ''}`;
}

interface ReadState {
  /** Tickets handed out for this shape so far. */
  issued: number;
  /** The tickets of the reads still waiting for the server. */
  open: Set<number>;
  /** The ticket of the newest read that has ended (0: none yet). */
  settledTicket: number;
  /** When that read ended (ms), null before any read ended. */
  settledAt: number | null;
  /** Whether that read failed. */
  failed: boolean;
}

/**
 * Session-scoped last answers, keyed by the query shape. Page mounts and tab switches render the
 * cached list INSTANTLY (stale-while-revalidate) and refresh quietly, instead of sitting on
 * "Loading..." for a full round-trip every navigation (rev7, William: list load times). Small and
 * bounded by the handful of shapes the pages actually use.
 */
const answers = new Map<string, ListResp>();
const reads = new Map<string, ReadState>();
const listeners = new Set<() => void>();

/** The last good answer for this shape, if any. */
export function cachedAnswer(key: string): ListResp | undefined {
  return answers.get(key);
}

/** A read of this shape is starting. Returns its ticket, to hand back when the read ends. */
export function readStarted(key: string): number {
  let s = reads.get(key);
  if (!s) {
    s = { issued: 0, open: new Set(), settledTicket: 0, settledAt: null, failed: false };
    reads.set(key, s);
  }
  s.issued += 1;
  s.open.add(s.issued);
  return s.issued;
}

/**
 * A read came back with an answer. The answer is kept whatever its ticket (rev7's rule, above); it is
 * the newest outcome unless a read that started later has already ended.
 */
export function readSucceeded(key: string, ticket: number, resp: ListResp, at: number = Date.now()): void {
  if (!close(key, ticket, false, at)) return;
  answers.set(key, resp);
  notify();
}

/** A read failed. The last good answer stays; the shape's newest outcome becomes "failed". */
export function readFailed(key: string, ticket: number, at: number = Date.now()): void {
  if (!close(key, ticket, true, at)) return;
  notify();
}

/** Ends one read. False when the ticket is not in flight (already reported, or issued before a reset). */
function close(key: string, ticket: number, failed: boolean, at: number): boolean {
  const s = reads.get(key);
  if (!s || !s.open.delete(ticket)) return false;
  if (ticket >= s.settledTicket) {
    s.settledTicket = ticket;
    s.settledAt = at;
    s.failed = failed;
  }
  return true;
}

function notify(): void {
  for (const listener of Array.from(listeners)) {
    // One reader's fault must never break a page's read (the page's own update runs after this).
    try {
      listener();
    } catch {
      /* a listener that throws is that listener's bug; the read itself has landed */
    }
  }
}

/** How the newest read of this shape ended, and how many are in flight. */
export interface ReadOutcome {
  inflight: number;
  settledAt: number | null;
  failed: boolean;
}

export function readOutcome(key: string): ReadOutcome {
  const s = reads.get(key);
  return s
    ? { inflight: s.open.size, settledAt: s.settledAt, failed: s.failed }
    : { inflight: 0, settledAt: null, failed: false };
}

/** Called after every read of any shape ends. Returns the unsubscribe. */
export function subscribeQueueCache(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Test seam: forget every answer and outcome (mounted readers stay subscribed). */
export function resetQueueCacheForTests(): void {
  answers.clear();
  reads.clear();
}
